import { FormatGroup, FormatOption } from "./types";
import { exportDatasetMessages } from "./constants";

export function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${Math.floor((n / 1_000_000) * 10) / 10}M`;
    if (n >= 1_000) return `${Math.floor((n / 1_000) * 10) / 10}k`;
    return n.toLocaleString();
}

/**
 * Maps internal format values to SDK-compatible names.
 * The SDK uses shortened names (e.g., "yolov5" not "yolov5pytorch")
 * because the SDK predates the internal naming convention.
 */
export function toSdkFormat(format: string): string {
    return format.replace("yolov5pytorch", "yolov5").replace("yolov7pytorch", "yolov7");
}

export function maskApiKey(key: string): string {
    if (key.length <= 6) return "*".repeat(key.length);
    return key.slice(0, 3) + "*".repeat(key.length - 6) + key.slice(-3);
}

/** Masks the `key=` query param value in a URL string. */
export function maskUrlKey(url: string): string {
    return url.replace(/([?&]key=)([^&]+)/, (_, prefix, value) => prefix + maskApiKey(value));
}

/**
 * Builds a Python SDK snippet for downloading a dataset version.
 *
 * Note: this generates a *version export* snippet (`project.version(1).download()`),
 * which is a different operation than the search export the modal triggers.
 * The SDK has no search-export equivalent yet — see D5 in the deferral plan.
 */
export function buildSnippet(
    format: string,
    workspaceUrl?: string,
    datasetName?: string,
    apiKey?: string | null,
    masked?: boolean
): string {
    const key = apiKey || "YOUR_API_KEY";
    const displayKey = masked && apiKey ? maskApiKey(key) : key;
    return `!pip install roboflow

from roboflow import Roboflow
rf = Roboflow(api_key="${displayKey}")
project = rf.workspace("${workspaceUrl || "your-workspace"}").project("${datasetName || "your-dataset"}")
dataset = project.version(1).download("${toSdkFormat(format)}")`;
}

export function buildRotatingMessages(imageCount?: number, formatLabel?: string | null): string[] {
    const countStr = imageCount ? formatNumber(imageCount) : "";
    const contextual =
        countStr && formatLabel
            ? `Packaging ${countStr} images in ${formatLabel} format...`
            : countStr
              ? `Packaging ${countStr} images...`
              : "Packaging your images and annotations...";
    return [contextual, ...exportDatasetMessages.rotating];
}

/** Finds a {@link FormatOption} by value across all groups. Returns `null` if not found. */
export function resolveFormat(format: string, formatGroups: FormatGroup[]): FormatOption | null {
    for (const group of formatGroups) {
        const found = group.formats.find((f) => f.value === format);
        if (found) return found;
    }
    return null;
}

export interface QueryFilterGroup {
    prefix: string;
    values: string[];
}

/**
 * Parses a search query into grouped filter tokens.
 * Handles quoted multi-word values (e.g., `class:"control flow"`).
 * Groups values sharing the same prefix into one entry.
 */
export function parseQueryGroups(query: string): QueryFilterGroup[] {
    const grouped = new Map<string, string[]>();
    const regex = /(\S+?:"[^"]*"|\S+)/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(query)) !== null) {
        const token = match[1];
        const colonIdx = token.indexOf(":");
        const prefix = colonIdx > 0 ? token.slice(0, colonIdx) : "";
        const raw = colonIdx > 0 ? token.slice(colonIdx + 1) : token;
        const value = raw.replace(/^"|"$/g, "");
        if (!grouped.has(prefix)) grouped.set(prefix, []);
        grouped.get(prefix)!.push(value);
    }

    return Array.from(grouped, ([prefix, values]) => ({ prefix, values }));
}
